/* Plugins. */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';
import { formValueSelector } from 'redux-form';
import { connect } from 'react-redux';
import Row from 'react-bootstrap/lib/Row';
import Col from 'react-bootstrap/lib/Col';
import FormGroup from 'react-bootstrap/lib/FormGroup';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import withStyles from 'isomorphic-style-loader/lib/withStyles';
import cx from 'classnames';

import CurrencyConverter from '../../CurrencyConverter';
import PromoCodeInput from './PromoCodeInput';
import messages from '../../../locale/messages';
import { fetchBillingCalculation } from '../../../actions/booking/fetchBillingCalculation';
import { clearBillingCalculation } from '../../../actions/booking/billingCalculation';
import showToaster from '../../../helpers/toasterMessages/showToaster';

import Faq from './question.svg';

import s from './Calendar.css';
import cs from '../../commonStyle.css';

class BillDetails extends Component {
  static propTypes = {
    listId: PropTypes.number.isRequired,
    delivery: PropTypes.number,
    currency: PropTypes.string.isRequired,
    startDate: PropTypes.object.isRequired,
    endDate: PropTypes.object.isRequired,
    guests: PropTypes.number,
    startTime: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    endTime: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    isDeliveryIncluded: PropTypes.bool,
    fetchBillingCalculation: PropTypes.func.isRequired,
    clearBillingCalculation: PropTypes.func.isRequired,
    billingCalculation: PropTypes.object,
    appliedPromoCode: PropTypes.string,
  };

  constructor(props) {
    super(props);
    this.state = {
      isApplying: false,
    };
    this.lastRequestKey = null;
  }

  componentDidMount() {
    this.loadBilling();
  }

  componentDidUpdate(prevProps) {
    const tripChanged =
      prevProps.listId !== this.props.listId ||
      !this.isSameDay(prevProps.startDate, this.props.startDate) ||
      !this.isSameDay(prevProps.endDate, this.props.endDate) ||
      prevProps.guests !== this.props.guests ||
      prevProps.currency !== this.props.currency ||
      this.normalizeTime(prevProps.startTime) !== this.normalizeTime(this.props.startTime) ||
      this.normalizeTime(prevProps.endTime) !== this.normalizeTime(this.props.endTime) ||
      Boolean(prevProps.isDeliveryIncluded) !== Boolean(this.props.isDeliveryIncluded);

    if (tripChanged) {
      // Drop stale totals immediately so the listing header cannot show a previous trip.
      this.props.clearBillingCalculation();
      this.lastRequestKey = null;
      this.loadBilling();
    }
  }

  // Number(undefined) is NaN, and NaN !== NaN is always true — that caused an update loop.
  normalizeTime = (value) => {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
  };

  isSameDay = (a, b) => {
    if (!a && !b) return true;
    if (!a || !b) return false;
    const aMs = a.valueOf ? a.valueOf() : new Date(a).valueOf();
    const bMs = b.valueOf ? b.valueOf() : new Date(b).valueOf();
    return aMs === bMs;
  };

  getRequestKey = (promoCode) => {
    const {
      listId,
      startDate,
      endDate,
      guests,
      currency,
      startTime,
      endTime,
      isDeliveryIncluded,
      appliedPromoCode,
    } = this.props;

    const code = promoCode !== undefined ? promoCode : appliedPromoCode;

    return [
      listId,
      startDate && startDate.valueOf(),
      endDate && endDate.valueOf(),
      guests,
      currency,
      Number(startTime),
      Number(endTime),
      isDeliveryIncluded,
      code || '',
    ].join('|');
  };

  loadBilling = async (promoCodeOverride) => {
    const {
      listId,
      startDate,
      endDate,
      guests,
      currency,
      startTime,
      endTime,
      isDeliveryIncluded,
      appliedPromoCode,
      fetchBillingCalculation,
    } = this.props;

    if (!startDate || !endDate || !listId) {
      return;
    }

    const requestKey = this.getRequestKey(promoCodeOverride);
    if (requestKey === this.lastRequestKey) {
      return;
    }

    this.lastRequestKey = requestKey;
    this.setState({ isApplying: true });

    try {
      const response = await fetchBillingCalculation({
        listId,
        startDate,
        endDate,
        guests: guests || 1,
        convertCurrency: currency,
        startTime: Number(startTime),
        endTime: Number(endTime),
        isDeliveryIncluded,
        promoCode: promoCodeOverride,
        appliedPromoCode: promoCodeOverride === undefined ? appliedPromoCode : undefined,
      });

      if (!response || response.status !== 200) {
        this.lastRequestKey = null;
      }
    } finally {
      this.setState({ isApplying: false });
    }
  };

  handlePromoApply = (rawCode) => {
    const trimmedCode = rawCode ? String(rawCode).trim() : '';

    if (!trimmedCode) {
      showToaster({ messageId: 'promoEnterCode', toasterType: 'error' });
      return;
    }

    this.lastRequestKey = null;
    this.loadBilling(trimmedCode);
  };

  handlePromoRemove = () => {
    this.lastRequestKey = null;
    this.loadBilling(null);
  };

  renderPromoTooltip = () => {
    const { formatMessage } = this.props.intl;

    return (
      <OverlayTrigger
        overlay={
          <Tooltip className={s.tooltip} id="promo-discount-tooltip">
            {formatMessage(messages.promoDiscountTooltip)}
          </Tooltip>
        }
        placement="top"
        delayShow={300}
        delayHide={150}
      >
        <span className={s.promoTooltipIcon}>
          <img src={Faq} className={cx(s.faqImage, 'faqImageRTL')} alt="" />
        </span>
      </OverlayTrigger>
    );
  };

  renderCharges = () => {
    const { formatMessage } = this.props.intl;
    const { currency, billingCalculation } = this.props;
    const billing = billingCalculation?.result;

    if (!billing) {
      return null;
    }

    const {
      averagePrice,
      days,
      priceForDays,
      delivery,
      guestServiceFee,
      discount,
      discountLabel,
      promoDiscount,
      promoLabel,
      securityDeposit,
      total,
    } = billing;

    const promoDiscountAmount = Number(promoDiscount) || 0;
    const securityDepositAmount = Number(securityDeposit) || 0;
    const subtotal =
      securityDepositAmount > 0
        ? Number(total) - securityDepositAmount
        : Number(total);

    return (
      <>
        <p className={cx(s.reservationChargesTitle, cs.commonMediumText, cs.fontWeightMedium, cs.spaceBottom2)}>
          <FormattedMessage {...messages.reservationCharges} />
        </p>
        <Row>
          <Col xs={12} sm={12} md={12} lg={12} className={'viewListingCalendarTable'}>
            <table className={'table'}>
              <tbody>
                <tr className={cx(s.positionR)}>
                  <td className={cx(s.noBorder, 'textAlignRightRTL')}>
                    <div className={cx(s.specialPriceText, 'directionLtr')}>
                      <CurrencyConverter amount={averagePrice} from={currency} />
                      {' x'} {days}{' '}
                      {days > 1 ? formatMessage(messages.nights) : formatMessage(messages.night)}
                    </div>
                  </td>
                  <td className={cx(s.noBorder, 'text-right', 'textAlignLeftRTL')}>
                    <CurrencyConverter amount={priceForDays} from={currency} />
                  </td>
                </tr>
                {Number(delivery) > 0 && this.props.isDeliveryIncluded && (
                  <tr>
                    <td className="textAlignRightRTL">
                      <FormattedMessage {...messages.cleaningFee} />
                    </td>
                    <td className={cx('text-right', 'textAlignLeftRTL')}>
                      <CurrencyConverter amount={delivery} from={currency} />
                    </td>
                  </tr>
                )}
                {Number(guestServiceFee) > 0 && (
                  <tr>
                    <td className="textAlignRightRTL">
                      <FormattedMessage {...messages.serviceFee} />
                    </td>
                    <td className={cx('text-right', 'textAlignLeftRTL')}>
                      <CurrencyConverter amount={guestServiceFee} from={currency} />
                    </td>
                  </tr>
                )}
                {Number(discount) > 0 && (
                  <tr>
                    <td className="textAlignRightRTL">{discountLabel}</td>
                    <td className={cx('text-right', s.discountText, 'textAlignLeftRTL')}>
                      -{' '}
                      <CurrencyConverter amount={discount} from={currency} />
                    </td>
                  </tr>
                )}
                {promoDiscountAmount > 0 && (
                  <tr>
                    <td className="textAlignRightRTL">
                      {this.renderPromoTooltip()}
                      {promoLabel || formatMessage(messages.promoDiscount)}
                    </td>
                    <td className={cx('text-right', s.discountText, 'textAlignLeftRTL')}>
                      -{' '}
                      <CurrencyConverter amount={promoDiscountAmount} from={currency} />
                    </td>
                  </tr>
                )}
                <tr className={s.subtotalRow}>
                  <td className="textAlignRightRTL">
                    <FormattedMessage {...messages.subTotal} />
                  </td>
                  <td className={cx('text-right', 'textAlignLeftRTL')}>
                    <CurrencyConverter amount={subtotal} from={currency} />
                  </td>
                </tr>
                {securityDepositAmount > 0 && (
                  <tr>
                    <td className="textAlignRightRTL">
                      <FormattedMessage {...messages.securityDeposit} />
                    </td>
                    <td className={cx('text-right', 'textAlignLeftRTL')}>
                      <CurrencyConverter amount={securityDepositAmount} from={currency} />
                    </td>
                  </tr>
                )}
                <tr className={s.totalRow}>
                  <td className="textAlignRightRTL">
                    <FormattedMessage {...messages.total} />
                  </td>
                  <td className={cx('text-right', 'textAlignLeftRTL')}>
                    <CurrencyConverter amount={total} from={currency} />
                  </td>
                </tr>
              </tbody>
            </table>
          </Col>
        </Row>
      </>
    );
  };

  render() {
    const { billingCalculation, appliedPromoCode } = this.props;
    const { isApplying } = this.state;
    const billingResult = billingCalculation?.result;

    return (
      <FormGroup>
        <PromoCodeInput
          appliedPromoCode={appliedPromoCode}
          isPromoApplied={billingResult?.isPromoValid === true}
          promoErrorMessage={billingResult?.promoErrorMessage}
          isApplying={isApplying}
          onApply={this.handlePromoApply}
          onRemove={this.handlePromoRemove}
        />
        {this.renderCharges()}
      </FormGroup>
    );
  }
}

const selector = formValueSelector('BookingForm');

const mapState = (state) => ({
  startTime: Number(selector(state, 'startTime')) || 0,
  endTime: Number(selector(state, 'endTime')) || 0,  billingCalculation: state?.book?.billingCalculation,
  appliedPromoCode: state?.book?.appliedPromoCode,
});

const mapDispatch = {
  fetchBillingCalculation,
  clearBillingCalculation,
};

export default injectIntl(withStyles(s, cs)(connect(mapState, mapDispatch)(BillDetails)));
